/**
 * Collection and search results: filters, sort, grid density and "Load more".
 *
 *   <facets-root data-section-id>  wraps one results section. Any change to the filter form or the
 *                                  sort select fetches the same section through the Section Rendering
 *                                  API, swaps every [data-facets-region], reconciles the product grid
 *                                  (GSAP Flip when motion is allowed) and pushes the URL.
 *   <grid-density>                 1/2 columns on small screens, 2/3/4 from 750px, saved per device
 *                                  class in Lumi.storage('lumi:grid-density').
 *
 * Everything here is an enhancement: without it the page is a GET form with links.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});
  const DENSITY_KEY = 'lumi:grid-density';
  const mqWide = window.matchMedia('(min-width: 750px)');
  const mqDesktop = window.matchMedia('(min-width: 990px)');

  const motionAllowed = () =>
    !!(window.gsap && window.Flip) &&
    document.documentElement.dataset.motion !== 'false' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let flipReady = false;
  function flip() {
    if (!flipReady && window.gsap && window.Flip) {
      window.gsap.registerPlugin(window.Flip);
      flipReady = true;
    }
    return window.Flip;
  }

  // Section Rendering responses carry section_id in their generated links; never expose it.
  function cleanHref(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  function cleanLinks(root) {
    root.querySelectorAll('a[href*="section_id"]').forEach((a) => a.setAttribute('href', cleanHref(a.getAttribute('href'))));
    root.querySelectorAll('[data-next-url*="section_id"]').forEach((el) => (el.dataset.nextUrl = cleanHref(el.dataset.nextUrl)));
  }

  /* ---------- Grid density ---------- */
  const density = {
    read() {
      const saved = Lumi.storage ? Lumi.storage.get(DENSITY_KEY, null) : null;
      return saved && typeof saved === 'object' ? saved : {};
    },
    scope: () => (mqWide.matches ? 'desktop' : 'mobile'),
    allowed: (scope) => (scope === 'desktop' ? ['2', '3', '4'] : ['1', '2']),
    fallback: () => (!mqWide.matches ? '2' : mqDesktop.matches ? '4' : '3'),
    // The saved choice for the current screen, or null for the responsive default.
    current() {
      const scope = density.scope();
      const value = String(density.read()[scope]);
      return density.allowed(scope).includes(value) ? value : null;
    },
    save(scope, value) {
      const saved = density.read();
      saved[scope] = value;
      if (Lumi.storage) Lumi.storage.set(DENSITY_KEY, saved);
    },
  };

  // Cards are rendered with sizes for the default 4/3/2 grid; a denser or looser choice
  // changes how wide each image really is, so the browser should pick from srcset again.
  function fitImages(grid, cols) {
    grid.querySelectorAll('img[sizes]').forEach((img) => {
      if (!img.dataset.sizesDefault) img.dataset.sizesDefault = img.sizes;
      img.sizes = cols ? `${Math.ceil(100 / Number(cols))}vw` : img.dataset.sizesDefault;
    });
  }

  function setCols(grid, cols) {
    if (cols) grid.setAttribute('data-cols', cols);
    else grid.removeAttribute('data-cols');
    fitImages(grid, cols);
  }

  /* ---------- Flip helper ---------- */
  // Records the grid items, runs mutate() (which may hide leaving items with display:none and
  // insert entering ones), then animates everyone from the old layout to the new one. Leaving items
  // stay in the grid, marked data-leaving, until the Flip ends and removes them.
  function flipGrid(grid, mutate, { stagger = 0 } = {}) {
    if (!motionAllowed()) {
      const result = mutate() || {};
      (result.leaving || []).forEach((el) => el.remove());
      return;
    }
    const Flip = flip();
    const gsap = window.gsap;
    const state = Flip.getState(grid.children);
    const startHeight = grid.offsetHeight;
    const result = mutate() || {};
    const endHeight = grid.offsetHeight;
    gsap.killTweensOf(grid);
    gsap.fromTo(grid, { height: startHeight }, { height: endHeight, duration: 0.7, ease: 'power3.inOut', clearProps: 'height' });
    Flip.from(state, {
      targets: grid.children,
      absolute: true,
      duration: 0.7,
      ease: 'power3.inOut',
      stagger,
      onEnter: (els) => gsap.fromTo(els, { opacity: 0, scale: 0.94 }, { opacity: 1, scale: 1, duration: 0.55, delay: 0.25, clearProps: 'opacity,transform' }),
      onLeave: (els) => gsap.to(els, { opacity: 0, scale: 0.94, duration: 0.3 }),
      onComplete: () => (result.leaving || []).forEach((el) => el.remove()),
    });
  }

  // The grid's items, without the ones a running Flip is still animating out: a render or Load more
  // that lands inside the Flip must neither reuse nor count them.
  const gridItems = (grid) => [...grid.children].filter((li) => !li.hasAttribute('data-leaving'));

  /* ---------- <facets-root> ---------- */
  class FacetsRoot extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.path = window.location.pathname;
      this.lastSearch = window.location.search;
      this.setAttribute('data-enhanced', '');

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPop = this.onPop.bind(this);
      this.applySoon = Lumi.debounce(() => this.apply(), 500);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPop);

      this.setupDrawer();
      this.setupStuck();
      this.querySelectorAll('[data-price-range]').forEach((el) => this.syncSlider(el));
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      if (this.stuckObserver) this.stuckObserver.disconnect();
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    get drawer() {
      return this.querySelector('[data-facets-drawer]');
    }

    get grid() {
      return this.querySelector('[data-grid]');
    }

    /* ----- Drawer ----- */
    setupDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;
      drawer.addEventListener('close', () => {
        Lumi.lockScroll(false);
        Lumi.emit('lumi:dialog:closed', { id: drawer.id });
        this.flush();
      });
      // A click on the backdrop lands on the dialog itself, outside the panel box.
      drawer.addEventListener('click', (e) => {
        if (e.target !== drawer) return;
        const r = drawer.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) drawer.close();
      });
    }

    openDrawer(trigger) {
      const drawer = this.drawer;
      if (!drawer || drawer.open) return;
      this.trigger = trigger;
      drawer.showModal();
      Lumi.lockScroll(true);
      Lumi.emit('lumi:dialog:opened', { id: drawer.id });
    }

    closeDrawer() {
      if (this.drawer && this.drawer.open) this.drawer.close();
    }

    /* ----- Sticky toolbar state ----- */
    setupStuck() {
      const bar = this.querySelector('[data-facets-toolbar]');
      if (!bar || !('IntersectionObserver' in window)) return;
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;margin-bottom:-1px;';
      bar.before(sentinel);
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
      this.stuckObserver = new IntersectionObserver(
        ([entry]) => bar.toggleAttribute('data-stuck', !entry.isIntersecting && entry.boundingClientRect.top < headerHeight + 1),
        { rootMargin: `-${headerHeight + 1}px 0px 0px 0px` }
      );
      this.stuckObserver.observe(sentinel);
    }

    /* ----- Events ----- */
    onClick(e) {
      const open = e.target.closest('[data-facets-open]');
      if (open) {
        this.openDrawer(open);
        return;
      }
      if (e.target.closest('[data-facets-close]')) {
        this.closeDrawer();
        return;
      }
      const more = e.target.closest('[data-load-more]');
      if (more) {
        this.loadMore(more);
        return;
      }
      const link = e.target.closest('a[data-facets-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
        e.preventDefault();
        this.pending = false;
        this.render(cleanHref(link.href));
      }
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-price-input], [data-price-range-input]')) return; // handled on input
      if (t.matches('[data-facets-sort]') || (t.form && t.form === this.form) || t.closest('[data-facets-form]')) {
        this.pending = false;
        this.apply();
      }
    }

    onInput(e) {
      const t = e.target;
      const range = t.closest('[data-price-range]');
      if (!range) return;
      if (t.matches('[data-price-range-input]')) this.fromSlider(range, t);
      else if (t.matches('[data-price-input]')) this.syncSlider(range);
      this.pending = true;
      this.applySoon();
    }

    onSubmit(e) {
      if (e.target !== this.form) return;
      e.preventDefault();
      this.closeDrawer();
      this.flush();
    }

    onPop() {
      if (window.location.pathname !== this.path || window.location.search === this.lastSearch) return;
      this.render(window.location.pathname + window.location.search, { push: false });
    }

    flush() {
      if (!this.pending) return;
      this.pending = false;
      this.apply();
    }

    /* ----- Price slider ----- */
    fromSlider(range, input) {
      const max = parseFloat(range.dataset.max) || 0;
      const [lo, hi] = range.querySelectorAll('[data-price-range-input]');
      let a = parseFloat(lo.value);
      let b = parseFloat(hi.value);
      if (a > b) {
        if (input === lo) a = b;
        else b = a;
        lo.value = a;
        hi.value = b;
      }
      const minField = range.querySelector('[data-price-input="min"]');
      const maxField = range.querySelector('[data-price-input="max"]');
      minField.value = a > 0 ? a : '';
      maxField.value = b < max ? b : '';
      this.paintSlider(range, a, b, max);
    }

    syncSlider(range) {
      const max = parseFloat(range.dataset.max) || 0;
      const [lo, hi] = range.querySelectorAll('[data-price-range-input]');
      if (!lo || !hi) return;
      const minField = range.querySelector('[data-price-input="min"]');
      const maxField = range.querySelector('[data-price-input="max"]');
      const a = Math.min(Math.max(parseFloat(minField.value) || 0, 0), max);
      const b = Math.max(Math.min(maxField.value === '' ? max : parseFloat(maxField.value), max), a);
      lo.value = a;
      hi.value = b;
      this.paintSlider(range, a, b, max);
    }

    paintSlider(range, a, b, max) {
      const fill = range.querySelector('[data-price-slider]');
      if (!fill || !max) return;
      fill.style.setProperty('--from', `${(a / max) * 100}%`);
      fill.style.setProperty('--to', `${(b / max) * 100}%`);
    }

    /* ----- URL building ----- */
    buildUrl() {
      const form = this.form;
      const params = new URLSearchParams();
      if (form) {
        for (const [key, value] of new FormData(form)) {
          if (value === '') continue;
          params.append(key, value);
        }
      } else {
        // Filtering disabled: only the sort select (and search terms) drive the URL.
        const current = new URLSearchParams(window.location.search);
        ['q', 'options[prefix]'].forEach((k) => current.get(k) && params.set(k, current.get(k)));
        const sort = this.querySelector('[data-facets-sort]');
        if (sort) params.set('sort_by', sort.value);
      }
      const action = form ? new URL(form.getAttribute('action'), window.location.origin).pathname : window.location.pathname;
      const query = params.toString();
      return query ? `${action}?${query}` : action;
    }

    apply() {
      const url = this.buildUrl();
      if (url === window.location.pathname + window.location.search) return;
      this.render(url);
    }

    /* ----- Rendering ----- */
    async render(url, { push = true } = {}) {
      if (this.controller) this.controller.abort();
      const controller = (this.controller = new AbortController());
      const results = this.querySelector('[data-facets-results]');
      if (results) results.setAttribute('aria-busy', 'true');

      let html;
      try {
        html = await Lumi.fetchSection(url, this.sectionId, { signal: controller.signal });
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.assign(url);
        return;
      }
      if (controller !== this.controller) return;

      const doc = Lumi.parseHTML(html);
      const fresh = doc.querySelector('facets-root');
      if (!fresh) {
        window.location.assign(url);
        return;
      }
      cleanLinks(fresh);

      if (push) window.history.pushState({ facets: this.sectionId }, '', url);
      this.lastSearch = window.location.search;

      this.swapRegions(fresh);
      this.swapResults(fresh);
      this.syncSort(fresh);
      if (results) results.removeAttribute('aria-busy');

      const count = this.querySelector('[data-facets-region="count"]');
      if (count) Lumi.announce(count.textContent.trim());
      this.revealResults();
    }

    swapRegions(fresh) {
      const active = document.activeElement;
      const activeId = active && this.contains(active) ? active.id : null;
      const keep = active && active.closest && active.closest('[data-facets-keep]');

      const groups = {};
      fresh.querySelectorAll('[data-facets-region]').forEach((el) => {
        (groups[el.dataset.facetsRegion] = groups[el.dataset.facetsRegion] || []).push(el);
      });

      Object.entries(groups).forEach(([name, list]) => {
        const targets = this.querySelectorAll(`[data-facets-region="${name}"]`);
        targets.forEach((target, i) => {
          const source = list[i];
          if (!source) return;
          if (name === 'facets') {
            const open = new Set([...target.querySelectorAll('details')].filter((d) => d.open).map((d) => d.id));
            const closed = new Set([...target.querySelectorAll('details')].filter((d) => !d.open).map((d) => d.id));
            const scroll = target.scrollTop;
            const liveGroup = keep && keep.closest('details');
            if (liveGroup && target.contains(liveGroup)) {
              // Someone is typing a price: refresh every other group, leave that one untouched.
              [...target.querySelectorAll(':scope > details')].forEach((d) => {
                if (d === liveGroup) return;
                const twin = source.querySelector(`#${CSS.escape(d.id)}`);
                if (twin) d.replaceWith(document.importNode(twin, true));
              });
            } else {
              target.replaceChildren(...document.importNode(source, true).childNodes);
            }
            target.querySelectorAll('details').forEach((d) => {
              if (closed.has(d.id)) d.open = false;
              else if (open.has(d.id)) d.open = true;
            });
            target.scrollTop = scroll;
            target.querySelectorAll('[data-price-range]').forEach((el) => this.syncSlider(el));
          } else {
            target.innerHTML = source.innerHTML;
          }
        });
      });

      if (activeId && document.activeElement !== active) {
        const again = this.querySelector(`#${CSS.escape(activeId)}`);
        if (again) again.focus({ preventScroll: true });
      }
    }

    swapResults(fresh) {
      const current = this.querySelector('[data-facets-results]');
      const next = fresh.querySelector('[data-facets-results]');
      if (!current || !next) return;

      ['[data-results-before]', '[data-results-after]'].forEach((sel) => {
        const a = current.querySelector(sel);
        const b = next.querySelector(sel);
        if (a && b) a.innerHTML = b.innerHTML;
      });

      const grid = current.querySelector('[data-grid]');
      const nextGrid = next.querySelector('[data-grid]');
      if (!grid || !nextGrid) return;

      const existing = new Map(gridItems(grid).map((li) => [li.dataset.flipId, li]));
      const incoming = [...nextGrid.children];

      flipGrid(grid, () => {
        const kept = new Set();
        const nodes = incoming.map((li) => {
          const old = existing.get(li.dataset.flipId);
          if (old && !kept.has(old)) {
            kept.add(old);
            return old;
          }
          return document.importNode(li, true);
        });
        const leaving = [...grid.children].filter((li) => !kept.has(li));
        leaving.forEach((li) => {
          li.style.display = 'none';
          li.setAttribute('data-leaving', '');
        });
        grid.append(...nodes, ...leaving);
        return { leaving };
      });

      this.afterCards(grid);
    }

    syncSort(fresh) {
      const sort = this.querySelector('[data-facets-sort]');
      const freshSort = fresh.querySelector('[data-facets-sort]');
      if (sort && freshSort) sort.value = freshSort.value;
    }

    afterCards(container) {
      if (Lumi.wishlist && Lumi.wishlist.sync) Lumi.wishlist.sync(container);
      const densityEl = this.querySelector('grid-density');
      if (densityEl && densityEl.apply) densityEl.apply(false);
    }

    // After filtering from far down the page, bring the top of the results back into view.
    revealResults() {
      if (this.drawer && this.drawer.open) return;
      const results = this.querySelector('[data-facets-results]');
      const bar = this.querySelector('[data-facets-toolbar]');
      if (!results) return;
      const offset = (bar ? bar.getBoundingClientRect().bottom : 0) + 8;
      const top = results.getBoundingClientRect().top;
      if (top < offset - 40) {
        window.scrollTo({ top: window.scrollY + top - offset, behavior: motionAllowed() ? 'smooth' : 'auto' });
      }
    }

    /* ----- Load more ----- */
    async loadMore(button) {
      const url = button.dataset.nextUrl;
      const grid = this.grid;
      if (!url || !grid || button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      let doc;
      try {
        doc = Lumi.parseHTML(await Lumi.fetchSection(url, this.sectionId));
      } catch (err) {
        window.location.assign(url);
        return;
      }
      const fresh = doc.querySelector('facets-root');
      const nextGrid = fresh && fresh.querySelector('[data-grid]');
      if (!nextGrid) {
        window.location.assign(url);
        return;
      }
      cleanLinks(fresh);

      const have = new Set(gridItems(grid).map((li) => li.dataset.flipId));
      const added = [...nextGrid.children].filter((li) => !have.has(li.dataset.flipId)).map((li) => document.importNode(li, true));
      grid.append(...added);

      const after = this.querySelector('[data-results-after]');
      const freshAfter = fresh.querySelector('[data-results-after]');
      if (after && freshAfter) after.innerHTML = freshAfter.innerHTML;

      window.history.replaceState(window.history.state, '', cleanHref(url));
      this.lastSearch = window.location.search;
      this.afterCards(grid);

      if (motionAllowed() && added.length) {
        window.gsap.fromTo(added, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.06, ease: 'power3.out', clearProps: 'opacity,transform' });
      }
      const firstLink = added[0] && added[0].querySelector('a[href]');
      if (firstLink) firstLink.focus({ preventScroll: true });
      const status = this.querySelector('.facet-pagination__status');
      Lumi.announce(status ? status.textContent.trim() : '');
    }
  }

  /* ---------- <grid-density> ---------- */
  class GridDensity extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('facets-root') || document;
      this.onClick = (e) => {
        const btn = e.target.closest('[data-cols-value]');
        if (!btn) return;
        density.save(btn.dataset.scope, btn.dataset.colsValue);
        this.apply(true);
      };
      this.onMedia = () => this.apply(false);
      this.addEventListener('click', this.onClick);
      mqWide.addEventListener('change', this.onMedia);
      mqDesktop.addEventListener('change', this.onMedia);
      this.apply(false);
    }

    disconnectedCallback() {
      mqWide.removeEventListener('change', this.onMedia);
      mqDesktop.removeEventListener('change', this.onMedia);
    }

    apply(animate) {
      const cols = density.current();
      const shown = cols || density.fallback();
      const scope = density.scope();
      this.querySelectorAll('[data-cols-value]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.scope === scope && btn.dataset.colsValue === shown));
      });
      const grid = this.root.querySelector('[data-grid]');
      if (!grid) return;
      if ((grid.getAttribute('data-cols') || null) === cols) {
        fitImages(grid, cols);
        return;
      }
      if (animate && grid.children.length) flipGrid(grid, () => setCols(grid, cols), { stagger: 0.012 });
      else setCols(grid, cols);
    }
  }

  if (!customElements.get('grid-density')) customElements.define('grid-density', GridDensity);
  if (!customElements.get('facets-root')) customElements.define('facets-root', FacetsRoot);
})();
